import {
  activityListResponseSchema,
  activitySchema,
  callLogListResponseSchema,
  callLogSchema,
  customerAddressSchema,
  customerContactSchema,
  customerListResponseSchema,
  customerNoteListResponseSchema,
  customerNoteSchema,
  customerSchema,
  customerDocumentListResponseSchema,
  type ActivityListResponse,
  type ActivitySource,
  type ActivityType,
  type ActivityWriteInput,
  type Activity,
  type CallLog,
  type CallLogInput,
  type Customer,
  type DocumentSummary,
  type CustomerAddress,
  type CustomerAddressInput,
  type CustomerAddressUpdateInput,
  type CustomerContact,
  type CustomerContactInput,
  type CustomerContactUpdateInput,
  type CustomerCreateInput,
  type CustomerListResponse,
  type CustomerNote,
  type CustomerNoteInput,
  type CustomerUpdateInput,
} from '@erp/shared';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { z } from 'zod';
import { apiDownload, apiFetch, apiSend, saveBlob, type ApiError } from '@/lib/api-client';

export interface CustomerListParams {
  page: number;
  pageSize: number;
  sort: string;
  q?: string | undefined;
  status?: string[];
  industry?: string[];
  industryCode?: string | undefined;
  tags?: string[];
  countryCode?: string | undefined;
  vatIdStatus?: string[];
}

export function buildCustomerQuery(params: CustomerListParams): string {
  const search = new URLSearchParams({
    page: String(params.page),
    pageSize: String(params.pageSize),
    sort: params.sort,
  });

  if (params.q !== undefined && params.q.length > 0) {
    search.set('q', params.q);
  }
  if (params.status !== undefined && params.status.length > 0) {
    search.set('status', params.status.join(','));
  }
  if (params.industryCode !== undefined && params.industryCode.trim() !== '') {
    search.set('industryCode', params.industryCode.trim());
  }

  if (params.industry !== undefined && params.industry.length > 0) {
    search.set('industry', params.industry.join(','));
  }
  if (params.tags !== undefined && params.tags.length > 0) {
    search.set('tags', params.tags.join(','));
  }
  if (params.countryCode !== undefined && params.countryCode.length > 0) {
    search.set('countryCode', params.countryCode);
  }
  if (params.vatIdStatus !== undefined && params.vatIdStatus.length > 0) {
    search.set('vatIdStatus', params.vatIdStatus.join(','));
  }

  return search.toString();
}

export const customerKeys = {
  all: ['customers'] as const,
  list: (params: CustomerListParams) => ['customers', 'list', params] as const,
  detail: (id: string) => ['customers', 'detail', id] as const,
  addresses: (id: string) => ['customers', 'detail', id, 'addresses'] as const,
  contacts: (id: string) => ['customers', 'detail', id, 'contacts'] as const,
  notes: (id: string) => ['customers', 'detail', id, 'notes'] as const,
  calls: (id: string) => ['customers', 'detail', id, 'calls'] as const,
  activities: (id: string) => ['customers', 'detail', id, 'activities'] as const,
  documents: (id: string) => ['customers', 'detail', id, 'documents'] as const,
};

export interface CustomerActivityFilters {
  types?: ActivityType[];
  sources?: ActivitySource[];
  q?: string;
  direction?: 'INBOUND' | 'OUTBOUND';
  personalOnly?: boolean;
}

const addressListSchema = z.object({ data: z.array(customerAddressSchema) });
const contactListSchema = z.object({ data: z.array(customerContactSchema) });

export function useCustomerList(
  params: CustomerListParams,
): UseQueryResult<CustomerListResponse, ApiError> {
  return useQuery({
    queryKey: customerKeys.list(params),
    queryFn: () =>
      apiFetch(`/api/v1/customers?${buildCustomerQuery(params)}`, customerListResponseSchema),
    placeholderData: (previous) => previous,
  });
}

export function useCustomer(id: string): UseQueryResult<Customer, ApiError> {
  return useQuery({
    queryKey: customerKeys.detail(id),
    queryFn: () => apiFetch(`/api/v1/customers/${id}`, customerSchema),
  });
}

export function useCreateCustomer(): UseMutationResult<Customer, ApiError, CustomerCreateInput> {
  const queryClient = useQueryClient();

  return useMutation<Customer, ApiError, CustomerCreateInput>({
    mutationFn: (input) =>
      apiFetch('/api/v1/customers', customerSchema, { method: 'POST', body: input }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: customerKeys.all });
    },
  });
}

export function useUpdateCustomer(
  id: string,
): UseMutationResult<Customer, ApiError, CustomerUpdateInput> {
  const queryClient = useQueryClient();

  return useMutation<Customer, ApiError, CustomerUpdateInput>({
    mutationFn: (input) =>
      apiFetch(`/api/v1/customers/${id}`, customerSchema, { method: 'PATCH', body: input }),
    onSuccess: (customer) => {
      queryClient.setQueryData(customerKeys.detail(id), customer);
      void queryClient.invalidateQueries({ queryKey: customerKeys.all });
    },
  });
}

export function useDeleteCustomer(): UseMutationResult<void, ApiError, string> {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, string>({
    mutationFn: (id) => apiSend(`/api/v1/customers/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: customerKeys.all });
    },
  });
}

export function useCustomerExport(): UseMutationResult<
  void,
  ApiError,
  { params: CustomerListParams; format: 'csv' | 'xlsx' }
> {
  return useMutation<void, ApiError, { params: CustomerListParams; format: 'csv' | 'xlsx' }>({
    mutationFn: async ({ params, format }) => {
      const query = buildCustomerQuery({ ...params, page: 1, pageSize: 200 });
      const result = await apiDownload(
        `/api/v1/customers/export?${query}&format=${format}`,
        `kunden.${format}`,
      );
      saveBlob(result);
    },
  });
}

// ---------------------------------------------------------------------------
// Adressen und Ansprechpartner
// ---------------------------------------------------------------------------

export function useCustomerAddresses(id: string): UseQueryResult<CustomerAddress[], ApiError> {
  return useQuery({
    queryKey: customerKeys.addresses(id),
    queryFn: async () => {
      const response = await apiFetch(`/api/v1/customers/${id}/addresses`, addressListSchema);
      return response.data;
    },
    enabled: id !== '',
  });
}

export function useCreateAddress(
  customerId: string,
): UseMutationResult<CustomerAddress, ApiError, CustomerAddressInput> {
  const queryClient = useQueryClient();

  return useMutation<CustomerAddress, ApiError, CustomerAddressInput>({
    mutationFn: (input) =>
      apiFetch(`/api/v1/customers/${customerId}/addresses`, customerAddressSchema, {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: customerKeys.addresses(customerId) });
    },
  });
}

export function useUpdateAddress(
  customerId: string,
): UseMutationResult<
  CustomerAddress,
  ApiError,
  { addressId: string; input: CustomerAddressUpdateInput }
> {
  const queryClient = useQueryClient();
  return useMutation<
    CustomerAddress,
    ApiError,
    { addressId: string; input: CustomerAddressUpdateInput }
  >({
    mutationFn: ({ addressId, input }) =>
      apiFetch(`/api/v1/customers/${customerId}/addresses/${addressId}`, customerAddressSchema, {
        method: 'PATCH',
        body: input,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: customerKeys.addresses(customerId) });
    },
  });
}

export function useDeleteAddress(customerId: string): UseMutationResult<void, ApiError, string> {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, string>({
    mutationFn: (addressId) =>
      apiSend(`/api/v1/customers/${customerId}/addresses/${addressId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: customerKeys.addresses(customerId) });
    },
  });
}

export function useCustomerContacts(id: string): UseQueryResult<CustomerContact[], ApiError> {
  return useQuery({
    queryKey: customerKeys.contacts(id),
    queryFn: async () => {
      const response = await apiFetch(`/api/v1/customers/${id}/contacts`, contactListSchema);
      return response.data;
    },
    enabled: id !== '',
  });
}

export function useCreateContact(
  customerId: string,
): UseMutationResult<CustomerContact, ApiError, CustomerContactInput> {
  const queryClient = useQueryClient();

  return useMutation<CustomerContact, ApiError, CustomerContactInput>({
    mutationFn: (input) =>
      apiFetch(`/api/v1/customers/${customerId}/contacts`, customerContactSchema, {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: customerKeys.contacts(customerId) });
    },
  });
}

export function useUpdateContact(
  customerId: string,
): UseMutationResult<
  CustomerContact,
  ApiError,
  { contactId: string; input: CustomerContactUpdateInput }
> {
  const queryClient = useQueryClient();
  return useMutation<
    CustomerContact,
    ApiError,
    { contactId: string; input: CustomerContactUpdateInput }
  >({
    mutationFn: ({ contactId, input }) =>
      apiFetch(`/api/v1/customers/${customerId}/contacts/${contactId}`, customerContactSchema, {
        method: 'PATCH',
        body: input,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: customerKeys.contacts(customerId) });
    },
  });
}

export function useDeleteContact(customerId: string): UseMutationResult<void, ApiError, string> {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, string>({
    mutationFn: (contactId) =>
      apiSend(`/api/v1/customers/${customerId}/contacts/${contactId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: customerKeys.contacts(customerId) });
    },
  });
}

// ---------------------------------------------------------------------------
// Notizen, Telefonate, Aktivitäten, Belege
// ---------------------------------------------------------------------------

export function useCustomerNotes(id: string): UseQueryResult<CustomerNote[], ApiError> {
  return useQuery({
    queryKey: customerKeys.notes(id),
    queryFn: async () => {
      const response = await apiFetch(
        `/api/v1/customers/${id}/notes?pageSize=50`,
        customerNoteListResponseSchema,
      );
      return response.data;
    },
  });
}

export function useCreateNote(
  customerId: string,
): UseMutationResult<CustomerNote, ApiError, CustomerNoteInput> {
  const queryClient = useQueryClient();

  return useMutation<CustomerNote, ApiError, CustomerNoteInput>({
    mutationFn: (input) =>
      apiFetch(`/api/v1/customers/${customerId}/notes`, customerNoteSchema, {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: customerKeys.notes(customerId) });
      void queryClient.invalidateQueries({ queryKey: customerKeys.activities(customerId) });
    },
  });
}

export function useCustomerCalls(id: string): UseQueryResult<CallLog[], ApiError> {
  return useQuery({
    queryKey: customerKeys.calls(id),
    queryFn: async () => {
      const response = await apiFetch(
        `/api/v1/customers/${id}/calls?pageSize=50`,
        callLogListResponseSchema,
      );
      return response.data;
    },
  });
}

export function useCreateCall(
  customerId: string,
): UseMutationResult<CallLog, ApiError, CallLogInput> {
  const queryClient = useQueryClient();

  return useMutation<CallLog, ApiError, CallLogInput>({
    mutationFn: (input) =>
      apiFetch(`/api/v1/customers/${customerId}/calls`, callLogSchema, {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: customerKeys.calls(customerId) });
      void queryClient.invalidateQueries({ queryKey: customerKeys.activities(customerId) });
    },
  });
}

export function useCustomerActivities(
  id: string,
  filters: CustomerActivityFilters = {},
): UseQueryResult<ActivityListResponse, ApiError> {
  const search = new URLSearchParams({ pageSize: '50' });
  if (filters.types !== undefined && filters.types.length > 0) {
    search.set('type', filters.types.join(','));
  }
  if (filters.sources !== undefined && filters.sources.length > 0) {
    search.set('source', filters.sources.join(','));
  }
  if (filters.q !== undefined && filters.q.trim().length > 0) search.set('q', filters.q.trim());
  if (filters.direction !== undefined) search.set('direction', filters.direction);
  if (filters.personalOnly === true) search.set('personalOnly', 'true');

  return useQuery({
    queryKey: [...customerKeys.activities(id), filters],
    queryFn: () =>
      apiFetch(
        `/api/v1/customers/${id}/activities?${search.toString()}`,
        activityListResponseSchema,
      ),
  });
}

export function useCreateActivity(
  customerId: string,
): UseMutationResult<Activity, ApiError, ActivityWriteInput> {
  const queryClient = useQueryClient();
  return useMutation<Activity, ApiError, ActivityWriteInput>({
    mutationFn: (input) =>
      apiFetch(`/api/v1/customers/${customerId}/activities`, activitySchema, {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: customerKeys.activities(customerId) });
    },
  });
}

export function useCustomerDocuments(id: string): UseQueryResult<DocumentSummary[], ApiError> {
  return useQuery({
    queryKey: customerKeys.documents(id),
    queryFn: async () => {
      const response = await apiFetch(
        `/api/v1/customers/${id}/documents`,
        customerDocumentListResponseSchema,
      );
      return response.data;
    },
  });
}
